import { Component, OnInit, Input, ComponentRef } from '@angular/core';
import { Sprint } from '../../../models/sprint';
import { Project } from '../../../models/project';
import { ProjectService } from '../../../services/project.service';
import { SprintService } from '../../../services/sprint.service';
import { SettingsService } from '../../../services/settings.service';
import { IWidget } from '../../../interfaces/iWidget';
import { SettingsEventType } from '../../../models/enums/settingsEventType';
import { SynchronizationService } from '../../../services/synchronization.service';
import { EventProjectUpdated } from '../../../models/syncEvents/persistenceEvents/eventProjectUpdated';
import { SyncEventType } from '../../../models/enums/syncEventType';
import { Subscription } from 'rxjs';
import { ChannelService } from '../../../services/channel.service';
import { Channel } from '../../../models/channel';

@Component({
  selector: 'app-project-widget',
  templateUrl: './project-widget.component.html',
  styleUrls: ['./project-widget.component.scss']
  })
export class ProjectWidgetComponent implements IWidget, OnInit {
  public subscriptions: Subscription[] = [];
  public static widgetName = 'project';
  availableProjectKeys: string[];
  sprints: Sprint[];
  project: Project;
  public selectedProjectKey: string;
  public selectedSprintId: number;
  public isScrum: boolean;
  @Input() widget = true;
  public editModeProjectVision = false;
  public channel: Channel;

  // callbacks
  public setPosition;

  // ref
  public componentRef: ComponentRef<any>;
  public isUndocked: boolean;

  constructor(
    private projectService: ProjectService,
    private sprintService: SprintService,
    private settingsService: SettingsService,
    private syncService: SynchronizationService
  ) { }

  ngOnInit() {
    this.loadProjectAndSprintData();
    this.initProjectUpdatedSubscription();
  }

  ngOnDestroy(): void {
    for (const ev of this.subscriptions) {
      ev.unsubscribe();
    }

  }
  public getName() {
    return ProjectWidgetComponent.widgetName;
  }

  private initProjectUpdatedSubscription(): void {
    this.subscriptions.push(this.syncService.subscribeEvent<any>(SyncEventType.PROJECT_UPDATED).subscribe(payload => {
      const event = EventProjectUpdated.fromPayload(payload);
      // check if my project was changed
      if (Number(event.project.id) === Number(this.project.id)) {
        this.updateViewData(event.project);
      }
    }));
  }

  private updateViewData(updatedProject: Project){
    this.project = updatedProject;

  }

  public setUndocked(isUndocked: boolean) {
    this.isUndocked = isUndocked;
  }

  /**
   * Load all Projects to populate Project-Select-Options.
   * Load Sprints for current Project to populate the Sprint-Select-Options.
   * and joins the room for synchronization
   */
  loadProjectAndSprintData(): void {
    this.projectService.getKeysOfAvailableProjects().subscribe(projectKeys => {
      this.availableProjectKeys = projectKeys;
      this.tryRestoreLastSelectedProject();
      if (this.selectedProjectKey) {
        // TODO: Find a way to omit this request
        this.projectService.getBoardStates(this.selectedProjectKey).subscribe(bs => {
          this.isScrum = (bs.boardType === 'scrum' || bs.boardType === 'simple');
          this.settingsService.setProjectType(bs.boardType);
          this.loadProject(this.selectedProjectKey);
          if (this.isScrum) {
            this.loadSprintsForProject(this.selectedProjectKey);
          }
        });

      }
    });
  }

  /**
   * If there is no project selected we try set project that was selected last time
   */
  private tryRestoreLastSelectedProject() {
    const lastUsedProjectKey = this.settingsService.getProjectName();
    if (!this.selectedProjectKey && this.availableProjectKeys.includes(lastUsedProjectKey)) {
      this.selectedProjectKey = lastUsedProjectKey;
    }
    this.settingsService.emitSettingsUpdate(SettingsEventType.PROJECT_CHANGED, this.constructor.name);
  }

  private loadProject(projectKey: string): void {
    this.projectService.getProjectByKey(projectKey).subscribe((fetchedProject) => {
      this.project = fetchedProject;

      // join persistence room for project
      this.syncService.joinPersistenceRoom(this.selectedProjectKey);

    });
  }

  /**
   * Load all Sprints of a specific Project.
   */
  private loadSprintsForProject(projectKey: string): void {
    this.sprintService.getSprints(projectKey).subscribe(fetchedSprints => {
      this.sprints = fetchedSprints;

      // Reverse so we have to most recently created sprint on top
      this.sprints.reverse();
      this.tryRestoreLastSelectedSprint(projectKey);
    });
  }

  /**
   * If there is no sprint selected we try set sprint that was selected last time.
   */
  private tryRestoreLastSelectedSprint(projectKey: string) {
    if (!this.selectedSprintId) {
      const lastUsedSprintId = this.settingsService.getSprintId(projectKey);
      this.selectedSprintId = lastUsedSprintId;
    }
    this.settingsService.emitSettingsUpdate(SettingsEventType.SPRINT_CHANGED, this.constructor.name);
  }


  /**
   * Load new Project and reset selectedSprint.
   * Close team widget if project changed in dropdown.
   */
  onSelectedProjectChanged(): void {
    this.selectedSprintId = null;
    this.settingsService.setProjectKey(this.selectedProjectKey);
    this.loadProjectAndSprintData();
  }

  onSelectedSprintChanged(): void {
    this.settingsService.setSprintId(this.selectedProjectKey, this.selectedSprintId);
    this.settingsService.emitSettingsUpdate(SettingsEventType.SPRINT_CHANGED, this.constructor.name);
  }

  /**
   * Force Setting current sprint. This triggers an update event for the currently set sprint
   * even if this sprint is already set. Used to trigger a reload of issues for the already
   * selected sprint.
   */
  forceUpdate(): void {
    this.settingsService.setSprintId(this.selectedProjectKey, this.selectedSprintId);
    this.settingsService.emitSettingsUpdate(SettingsEventType.SPRINT_CHANGED, this.constructor.name);
  }

  changeProjectVision(event: any): void {
    this.projectService.updateProject(this.project).subscribe(() => {
      this.syncService.sendPersistenceEventNew(new EventProjectUpdated(this.project));

    });
    this.editModeProjectVision = !this.editModeProjectVision;
  }

  toggleProjectVisionEdit(event: any): void {
    this.editModeProjectVision = !this.editModeProjectVision;
  }
}
